import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Archive, Search, Lightbulb, ArrowRight, Loader2, X } from "lucide-react";
import { API } from "@/App";

export default function IdeaVault({ isOpen, onClose, onNavigateToBit }) {
  const [ideas, setIdeas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch vault ideas
  useEffect(() => {
    if (!isOpen) return;
    
    const fetchVault = async () => {
      setIsLoading(true);
      try {
        const url = debouncedSearch 
          ? `${API}/ideas/vault?search=${encodeURIComponent(debouncedSearch)}`
          : `${API}/ideas/vault`;
        
        const response = await fetch(url, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setIdeas(data);
        }
      } catch (error) {
        console.error("Failed to fetch vault:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchVault();
  }, [isOpen, debouncedSearch]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };

  const handleNavigateToBit = (bitId) => {
    onClose();
    if (onNavigateToBit) {
      onNavigateToBit(bitId);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[90vw] max-w-[700px] h-[80vh] max-h-[80vh] bg-card border-border flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
          <div className="flex items-center gap-3">
            <Archive className="w-5 h-5 text-primary" />
            <DialogTitle className="font-display text-xl uppercase tracking-wider">
              Idea Vault
            </DialogTitle>
            <Badge variant="outline" className="ml-auto">
              {ideas.length} idea{ideas.length !== 1 ? 's' : ''}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            Every spark you ever captured. Nothing is ever lost.
          </p>
        </DialogHeader>
        
        {/* Search */}
        <div className="px-6 py-3 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search your ideas..."
              className="pl-10 bg-background/50"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        
        {/* Ideas List */}
        <ScrollArea className="flex-1">
          <div className="p-6 space-y-3">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : ideas.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Archive className="w-12 h-12 mx-auto mb-4 opacity-30" />
                {searchQuery ? (
                  <>
                    <p>No ideas match &ldquo;{searchQuery}&rdquo;</p>
                    <p className="text-xs mt-1">Try a different search term</p>
                  </>
                ) : (
                  <>
                    <p>Your vault is empty</p>
                    <p className="text-xs mt-1">Ideas will appear here when you promote them to Write</p>
                  </>
                )}
              </div>
            ) : (
              ideas.map((idea) => (
                <div 
                  key={idea.idea_id}
                  className="border border-border rounded-sm p-4 bg-background/50 hover:bg-background/80 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-sm whitespace-pre-wrap break-words">
                        {idea.content}
                      </p>
                      
                      <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
                        <span>Captured {formatDate(idea.created_at)}</span>
                        {idea.archived_at && (
                          <>
                            <span>•</span>
                            <span>Archived {formatDate(idea.archived_at)}</span>
                          </>
                        )}
                      </div>
                      
                      {/* Link to bit */}
                      {idea.linked_bit && (
                        <button
                          onClick={() => handleNavigateToBit(idea.bit_id)}
                          className="mt-3 flex items-center gap-2 text-sm text-primary hover:underline"
                        >
                          <ArrowRight className="w-4 h-4" />
                          <span>
                            View bit: <strong>{idea.linked_bit.title || 'Untitled'}</strong>
                          </span>
                          {idea.linked_bit.status && (
                            <Badge variant="outline" className="text-xs ml-1">
                              {idea.linked_bit.status}
                            </Badge>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-border">
          <Button variant="outline" onClick={onClose} className="w-full">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
